import { builder } from '@builder.io/sdk';
import React, { useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import getConfig from 'next/config';
import { getPromotions } from '@/services/promo.service';
import useStore from '@/store/useStore';

import Layout from '@/components/Layout/Layout';
import MobileHeader from '@/components/Header/components/MobileHeader';
import Header from '@/components/Header/Header';
import Breadcrumbs from '@/components/BuilderIO/common/Breadcrumbs/Breadcrumbs';

import data from '@/data/transparency';
import ContentBlockLite from '@/components/mission/ContentBlockLite/ContentBlockLite';
import LargeCopySection from '@/components/mission/LargeCopySection/LargeCopySection';
import MissionNavTop from '@/components/mission/MissionNavTop';
import MissionHeroBanner from '@/components/mission/MissionHeroBanner/MissionHeroBanner';
import MissionNav from '@/components/mission/MissionNav';
import SlickCarousel from '@/components/Carousel/SlickCarousel';
import Map from '@/components/mission/Map/Map';
import ReportsSection from '@/components/mission/ReportsSection/ReportsSection';
import useStaticPageSetData from '@/hooks/useStaticPageSetData';
import { DeviceType } from '@/utils/utils';
import { renderMobileNavShellHtml, renderNavHtml } from '@/widgets/navLinks/renderNavHtml';

const { NEXT_PUBLIC_BUILDER_API_KEY } = getConfig().publicRuntimeConfig;

builder.init(NEXT_PUBLIC_BUILDER_API_KEY);

export async function getServerSideProps({ locale, req }: GetServerSidePropsContext) {
  const device = req?.headers['device_type'] as DeviceType;
  const formattedLocale = locale!.substring(0, 2) + locale!.substring(2, 5).toUpperCase();
  const navLink = 'nav-links';

  const builderData = await Promise.allSettled([
    builder.getAll(navLink),
    builder.getAll('footer-links-desktop'),
    builder.getAll('footer-links-mobile'),
    getPromotions(),
    builder
      .get('header-banner', {
        userAttributes: {
          locale: formattedLocale,
        },
      })
      .toPromise(),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    builder.getAll('mobile-nav-links').then(item => item.map(({ data }) => data)),
  ]);

  const navLinks = builderData[0].status === 'fulfilled' ? builderData[0].value : [];
  const footerLinks = builderData[1].status === 'fulfilled' ? builderData[1].value : [];
  const mobileFooterLinks = builderData[2].status === 'fulfilled' ? builderData[2].value : [];
  const promotions = builderData[3].status === 'fulfilled' ? builderData[3].value : {};
  const headerBanner = builderData[4].status === 'fulfilled' ? builderData[4].value : [];
  const mobileNavLinks = builderData[5].status === 'fulfilled' ? builderData[5].value : [];
  const desktopNavHtml = renderNavHtml({
    navLinks: navLinks.map(l => l.data),
    mobileNavLinks: [],
    promo: (promotions as any).topNav ?? null,
  }).desktopHtml;
  const mobileNavHtml = renderMobileNavShellHtml({ mobileNavLinks });

  return {
    props: {
      pageData: {
        header: {
          navLinks: navLinks.map(l => l.data),
          navHtml: desktopNavHtml,
          mobileNavHtml,
        },
        footer: {
          links: footerLinks.map(l => l.data),
          mobileLinks: mobileFooterLinks.map(l => l.data),
        },
        ...data,
        locale,
        promotions,
        headerBanner,
        mobileNavLinks,
        device,
      },
    },
  };
}

export default function Page({ pageData }: any) {
  useStaticPageSetData(pageData);
  const builderData = useStore(state => state.builderData);
  const setMobileNavLinks = useStore(state => state.setMobileNavLinks);

  useEffect(() => {
    setMobileNavLinks(pageData.mobileNavLinks);
  }, []);

  const ReportsSectionData = {
    body: 'Conflict Free & Ethical Diamond FAQs',
    faqData: [
      {
        question: 'What is a conflict free diamond?',
        answer: (
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-[16px] tw-flex-[1_0_0]">
            The Kimberley Process defines diamonds as “conflict-free” when they do not fund wars against legitimate
            governments.
          </div>
        ),
      },
      {
        question: 'Are conflict free diamonds real?',
        answer: (
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-[16px] tw-flex-[1_0_0]">
            Yes, conflict-free diamonds are natural diamonds that do not fund wars against legitimate governments.
          </div>
        ),
      },
      {
        question: 'What is an ethically sourced diamond?',
        answer: (
          <div>
            From our responsible lab diamond assortments to our transparently sourced natural diamonds, we offer a
            number of ways to find an ethically sourced diamond. Our{' '}
            <a href="/lab-grown-diamonds/renewable-energy/" className="tw-underline">
              Renewable Collection
            </a>{' '}
            and{' '}
            <a href="/lab-grown-diamonds/carbon-capture/" className="tw-underline">
              Capture Collection
            </a>{' '}
            showcase our pioneering planet-first lab diamond practices, while our Beyond Conflict Free Diamonds
            <span className="tw-align-top tw-text-[0.7em]">®</span> define what we consider ethical sourcing for
            natural diamonds: Surpassing Kimberley Process standards, our natural diamonds come from mine operations or
            countries that demonstrate a commitment to labor, trade, and environmental standards. An ethically sourced
            diamond meets high environmental standards for mining, supports social and economic development, and never
            contributes to violence, conflict, child labor, or forced labor.
          </div>
        ),
      },
    ],
  };

  return (
    <Layout
      page={pageData.layout}
      footerLinks={pageData.footer.links}
      mobileFooterLinks={pageData.footer.mobileLinks}
      builderData={builderData}
    >
      {/* Desktop Header */}
      <Header
        className="tw-hidden md:tw-block lg:tw-block"
        navLinks={pageData.header.navLinks}
        navHtml={pageData.header.navHtml}
      />
      {/* Mobile Header */}
      <MobileHeader
        className="lg:tw-hidden md:tw-hidden"
        mobileNavLinks={pageData.mobileNavLinks}
        mobileNavHtml={pageData.header.mobileNavHtml}
      />
      <div className="tw-mb-0 lg:tw-mb-0">
        <Breadcrumbs currentPageName={pageData.breadcrumbs.pageName} crumbs={pageData.breadcrumbs.crumbs} />
      </div>
      <MissionHeroBanner
        title={
          <span className="tw-inline-flex tw-flex-col">
            <span>Transparency</span>{' '}
            <span className="tw-mt-8 lg:tw-mt-6 tw-block tw-font-avenirmedium tw-text-be-base lg:tw-text-be-lg">
              BEYOND CONFLICT FREE DIAMONDS<span className="tw-align-top tw-text-[0.6em]">&reg;</span>
            </span>
          </span>
        }
        description={pageData.hero.description}
        textColor={pageData.hero.textColor}
        isHeader={pageData.hero.isHeader}
        hasIcon
        iconFill={pageData.hero.iconFill}
        background={pageData.hero.background}
        backgroundMobile={pageData.hero.backgroundMobile}
      />
      <MissionNavTop links={pageData.navLinks} />
      <LargeCopySection
        description={pageData.largeCopySectionOne.description}
        color={pageData.largeCopySectionOne.color}
        additionalClasses="tw-mb-14"
      />
      <Map title="Where we source" />
      <ContentBlockLite
        title={pageData.contentSectionOne.title}
        titleRichText={pageData.contentSectionOne.titleRichText}
        description={pageData.contentSectionOne.description}
        imageSources={pageData.contentSectionOne.imageSources}
        imageAlt={pageData.contentSectionOne.imageAlt}
        imageClassname="tw-w-[600px]"
        desktopMediaPosition={pageData.contentSectionOne.desktopMediaPosition}
        color={pageData.contentSectionOne.color}
        actions={pageData.contentSectionOne.actions}
      />
      <ContentBlockLite
        title={pageData.contentSectionTwo.title}
        titleRichText={pageData.contentSectionTwo.titleRichText}
        description={pageData.contentSectionTwo.description}
        imageSources={pageData.contentSectionTwo.imageSources}
        imageAlt={pageData.contentSectionTwo.imageAlt}
        imageClassname="tw-w-[600px]"
        desktopMediaPosition={pageData.contentSectionTwo.desktopMediaPosition}
        color={pageData.contentSectionTwo.color}
        actions={pageData.contentSectionTwo.actions}
      />
      <ContentBlockLite
        title={pageData.contentSectionThree.title}
        titleRichText={pageData.contentSectionThree.titleRichText}
        description={pageData.contentSectionThree.description}
        imageSources={pageData.contentSectionThree.imageSources}
        imageAlt={pageData.contentSectionThree.imageAlt}
        desktopMediaPosition={pageData.contentSectionThree.desktopMediaPosition}
        mobileMediaPosition={pageData.contentSectionThree.mobileMediaPosition}
        imageClassname="tw-w-[600px]"
        color={pageData.contentSectionThree.color}
        actions={pageData.contentSectionThree.actions}
        richTextClass="tw-text-left lg:tw-text-center lg:tw-w-3/4 lg:tw-mx-auto"
        titleClassName="lg:tw-text-center lg:tw-mb-6"
        classNames="lg:tw-mt-20"
      />
      <ContentBlockLite
        title={pageData.contentSectionFour.title}
        description={pageData.contentSectionFour.description}
        imageSources={pageData.contentSectionFour.imageSources}
        imageAlt={pageData.contentSectionFour.imageAlt}
        desktopMediaPosition={pageData.contentSectionFour.desktopMediaPosition}
        imageClassname="tw-w-[600px]"
        color={pageData.contentSectionFour.color}
        actions={pageData.contentSectionFour.actions}
      />
      <ContentBlockLite
        title={pageData.contentSectionFive.title}
        description={pageData.contentSectionFive.description}
        imageSources={pageData.contentSectionFive.imageSources}
        imageAlt={pageData.contentSectionFive.imageAlt}
        imageClassname="tw-w-[700px]"
        desktopMediaPosition={pageData.contentSectionFive.desktopMediaPosition}
        color={pageData.contentSectionFive.color}
        actions={pageData.contentSectionFive.actions}
      />
      <ContentBlockLite
        title={pageData.contentSectionSix.title}
        description={pageData.contentSectionSix.description}
        imageSources={pageData.contentSectionSix.imageSources}
        imageAlt={pageData.contentSectionSix.imageAlt}
        imageClassname="tw-w-[700px]"
        desktopMediaPosition={pageData.contentSectionSix.desktopMediaPosition}
        color={pageData.contentSectionSix.color}
        actions={pageData.contentSectionSix.actions}
      />

      <ContentBlockLite
        title={pageData.contentSectionSeven.title}
        description={pageData.contentSectionSeven.description}
        imageSources={pageData.contentSectionSeven.imageSources}
        imageAlt={pageData.contentSectionSeven.imageAlt}
        imageClassname="tw-w-[700px]"
        desktopMediaPosition={pageData.contentSectionSeven.desktopMediaPosition}
        color={pageData.contentSectionSeven.color}
        actions={pageData.contentSectionSeven.actions}
      />

      <ContentBlockLite
        title={pageData.contentSectionEight.title}
        description={pageData.contentSectionEight.description}
        imageSources={pageData.contentSectionEight.imageSources}
        imageAlt={pageData.contentSectionEight.imageAlt}
        imageClassname="tw-w-[700px]"
        desktopMediaPosition={pageData.contentSectionEight.desktopMediaPosition}
        color={pageData.contentSectionEight.color}
        actions={pageData.contentSectionEight.actions}
      />

      <ContentBlockLite
        title={pageData.contentSectionNine.title}
        description={pageData.contentSectionNine.description}
        imageSources={pageData.contentSectionNine.imageSources}
        imageAlt={pageData.contentSectionNine.imageAlt}
        imageClassname="tw-w-[700px]"
        desktopMediaPosition={pageData.contentSectionNine.desktopMediaPosition}
        color={pageData.contentSectionNine.color}
        actions={pageData.contentSectionNine.actions}
      />

      <MissionNav sectionClass="lg:tw-bg-color-light-green" links={pageData.navLinks} title="MORE ABOUT OUR MISSION" />
      <SlickCarousel {...pageData.moreToExplore} />
      <ReportsSection {...ReportsSectionData} classNames="tw-bg-color-white tw-relative" variant="faq" />
    </Layout>
  );
}
